import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import './Inventory.css'
import { useTranslation } from "react-i18next";
const CategorySlider = ({ categories = [], selectedCategory, sendSelectedCategory, slotPage }) => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(selectedCategory ? selectedCategory : 0);
    const [isAnimating, setIsAnimating] = useState(false);
    const { mode } = useThemeContext();

    // 🆕 Sync activeIndex with selectedCategory changes from parent
    useEffect(() => {
        if (selectedCategory && categories.length > 0) {
            const index = categories.findIndex(c => c.id === selectedCategory);
            if (index !== -1) {
                setActiveIndex(index);
            }
        }
    }, [selectedCategory, categories]);

    const handleCategoryClick = (relativeIndex) => {
        if (isAnimating) return;

        setIsAnimating(true);
        const newIndex = (activeIndex + relativeIndex + categories.length) % categories.length;

        setTimeout(() => {
            setActiveIndex(newIndex);
            setIsAnimating(false);
            const selectedCategory = categories[newIndex];
            sendSelectedCategory(selectedCategory); // Send the updated category to the parent
        }, 300); // Match transition duration
    };

    const getVisibleCategories = () => {
        const length = categories.length;
        const visibleCount = 7; // Number of visible categories
        if (length === 0) return []; // Handle empty categories array
        const startIndex = activeIndex - 1; // Highlight second item
        return Array.from({ length: visibleCount }, (_, i) =>
            categories[(startIndex + i + length) % length]
        );
    };

    const textColor = mode === 'dark' ? DARKCOLORS.DisabledLightBackground : LIGHTCOLORS.DisabledLightBackground;
    const textHighLightColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const maskBackgroundColor = mode === 'dark'
        ? "linear-gradient(270deg, rgba(27, 27, 27, 0.00) 0%, #1B1B1B 82.26%)"
        : "linear-gradient(270deg, rgba(236, 237, 248, 0.00) 0%, #ECEDF8 82.26%)";
    const rightMaskBackgroundColor = mode === 'dark'
        ? "linear-gradient(90deg, rgba(27, 27, 27, 0.00) 0%, #1B1B1B 82.26%)"
        : "linear-gradient(90deg, rgba(236, 237, 248, 0.00) 0%, #ECEDF8 82.26%)";

    return (
        <Box className="slider-sec"
            sx={{
                width: "100%",
                height: "100px",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                paddingLeft: "20px", // Align selected item to the left
                marginRight: "20px", // Align selected item to the left

            }}
        >
            {/* Left Mask */}
            {!slotPage && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "0",
                        left: "0",
                        width: "12%", // Adjust the size of the mask
                        height: "100%",
                        background: maskBackgroundColor,
                        pointerEvents: "none", // Disable pointer events for the mask
                        zIndex: 1,
                    }}
                />
            )}

            {/* Category container */}
            <Box className="slider-box-div"
                sx={{
                    display: "flex",
                    width: '100%',
                    justifyContent: "space-between",
                    flexGrow: 1, // Make the container flexible
                    position: "relative",
                    transition: "transform 0.3s ease",
                    "@media (max-width: 628px)": {
                        left: '-40px'
                    },
                    "@media (max-width: 500px)": {
                        left: '-70px'
                    },
                    "@media (max-width: 420px)": {
                        left: '-40px'
                    },
                }}
            >
                {categories.length === 0 ? (
                    <Box
                        sx={{
                            width: '100%',
                            textAlign: 'center',
                            color: textColor,
                            fontSize: '16px',
                            fontWeight: 500,
                        }}
                    >
                        {t('inventory.NoCategoryFound')}
                    </Box>
                ) : (
                    getVisibleCategories().map((category, index) => (
                        <Box
                            key={index}
                            sx={{
                                textAlign: "center",
                                cursor: "pointer",
                                flex: "1", // Each item takes equal width
                                color: index === 1 ? textHighLightColor : textColor,
                                marginTop: index === 1 ? "-10px" : "0px",
                                transform: index === 1 ? "scale(1.3)" : "scale(1)",
                                // Media Queries
                                "@media (max-width: 908px)": {
                                    transform: index === 1 ? "scale(1)" : "scale(0.9)",
                                    fontSize: "12px", // Adjusted font size for better readability
                                },
                                "@media (max-width: 620px)": {
                                    transform: index === 1 ? "scale(0.9)" : "scale(0.7)",
                                    fontSize: "10px", // Adjusted for better responsiveness
                                    minWidth: "120px",
                                },
                                "@media (max-width: 425px)": {
                                    transform: index === 1 ? "scale(0.9)" : "scale(0.7)",
                                    fontSize: "9px", // Adjusted for better responsiveness
                                    minWidth: "90px",
                                },

                                transition: "color 0.3s, transform 0.3s",

                            }}
                            onClick={() =>
                                handleCategoryClick(index - 1) // Adjust click logic for second place
                            }
                        >
                            <h3 style={{ margin: "0" }} className={index === 1 ? 'franchise-heading-text sliderItemtext' : 'popup-text'}>
                                {category?.name || category}
                            </h3>
                        </Box>
                    ))
                )}
            </Box>

            {/* Right Mask */}
            {!slotPage && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                        width: "15%", // Adjust the size of the mask
                        height: "100%",
                        background: rightMaskBackgroundColor,
                        pointerEvents: "none", // Disable pointer events for the mask
                        zIndex: 1,
                    }}
                />
            )}

            {/* Dot indicator */}
            {categories.length > 0 && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "10px",
                        left: "22.1%", // Align under the highlighted (second) item

                        "@media (max-width: 1000px)": {
                            left: "23.1%",

                        },
                        "@media (max-width: 850px)": {
                            left: "24.1%",

                        },
                        "@media (max-width: 628px)": {
                            left: "27.1%",

                        },
                        "@media (max-width: 508px)": {
                            left: "28.1%",

                        },
                        "@media (max-width: 426px)": {
                            left: "30.1%",

                        },
                        "@media (max-width: 376px)": {
                            left: "33.1%",

                        },
                        "@media (max-width: 340px)": {
                            left: "36.1%",

                        },
                        // "@media (max-width: 480px)": {
                        //     transform: index === 1 ? "scale(0.9)" : "scale(0.7)",
                        //     fontSize: "10px", // Adjusted for better responsiveness
                        // },
                    }}
                >
                    <FiberManualRecord sx={{ fontSize: "12px", color: textHighLightColor }} />
                </Box>
            )}
        </Box>
    );
};

export default CategorySlider;